import React from 'react';

export interface UnderlineTab<K extends string = string> {
  key: K;
  label: string;
  /** Optional leading icon; inherits the tab's text colour via currentColor. */
  icon?: React.ReactNode;
}

interface UnderlineTabNavProps<K extends string> {
  tabs: readonly UnderlineTab<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
}

/**
 * Reusable Figma underline tab bar: active tab = dark text + black underline,
 * inactive = muted. Controlled (active / onChange). Icons inherit currentColor
 * so they match the active/inactive text colour.
 */
export function UnderlineTabNav<K extends string>({
  tabs,
  active,
  onChange,
  className = '',
}: UnderlineTabNavProps<K>) {
  return (
    <div className={`flex items-center gap-6 ${className}`} role="tablist">
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className={`relative flex items-center gap-1.5 pb-2.5 text-[15px] font-medium transition-colors ${
              isActive ? 'text-[#171717]' : 'text-[#A3A3A3] hover:text-[#737373]'
            }`}
          >
            {t.icon}
            {t.label}
            {/* Selected underline — 2px #000, radius 2px, full tab width. Absolute
                bar (not border-b) so it can be rounded. */}
            {isActive && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-[2px] bg-black" />
            )}
          </button>
        );
      })}
    </div>
  );
}
